import useSWR from "swr";
import { Link } from "react-router-dom";
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";
import { ArrowUpRight, Inbox, PenLine, Send, Users } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { WeeklyRanking } from "@/components/WeeklyRanking";
import { Button } from "@/components/ui/button";
import { fetcher } from "@/lib/api";

const COLORS = ["#013060", "#52DDCF", "#0369A1", "#B45309", "#0F766E", "#6D28D9", "#4D7C0F", "#9D174D"];

function Metric({ label, value, hint, testid }) {
  return (
    <div className="border border-border bg-card p-5 hover:-translate-y-1 transition-transform" data-testid={testid}>
      <div className="kicker">{label}</div>
      <div className="text-3xl font-bold mt-2 mono">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { data, isLoading } = useSWR("/stats/overview", fetcher, { refreshInterval: 30000 });
  const t = data?.totals || {};

  return (
    <div>
      <PageHeader
        kicker="Panel de control"
        title="Estado de la sala de redacción"
        description="Métricas operativas y de mercadeo de PaginaUno.Do, LaSeguridadSocial.Do y demás medios de la agencia."
      >
        <Button asChild variant="outline" data-testid="go-ingesta-btn">
          <Link to="/ingesta"><Inbox className="h-4 w-4 mr-2" />Ingesta</Link>
        </Button>
        <Button asChild data-testid="go-redaccion-btn">
          <Link to="/redaccion"><PenLine className="h-4 w-4 mr-2" />Redacción</Link>
        </Button>
      </PageHeader>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando métricas…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4" data-testid="metrics-grid">
            <Metric label="Ingesta pendiente" value={t.ingesta_pendiente ?? 0} hint="Notas por procesar" testid="metric-ingesta" />
            <Metric label="Por revisar" value={t.pendientes_revision ?? 0} hint="Borradores generados" testid="metric-revision" />
            <Metric label="Publicadas" value={t.publicadas ?? 0} hint="En nuestros medios" testid="metric-publicadas" />
            <Metric label="Suscriptores" value={t.suscriptores ?? 0} hint="Lista activa" testid="metric-suscriptores" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Metric label="SEO promedio" value={t.seo_promedio ?? 0} hint="Puntaje de auditoría" testid="metric-seo" />
            <Metric label="Impresiones" value={t.impresiones_banners ?? 0} hint="Anuncios servidos" testid="metric-impresiones" />
            <Metric label="CTR anuncios" value={`${t.ctr_banners ?? 0}%`} hint={`${t.clics_banners ?? 0} clics`} testid="metric-ctr" />
            <Metric label="Boletines" value={t.campanas_enviadas ?? 0} hint="Campañas enviadas" testid="metric-boletines" />
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 border border-border bg-card p-6">
              <div className="kicker mb-4">Producción de los últimos 14 días</div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data?.serie_diaria || []}>
                  <CartesianGrid strokeDasharray="2 4" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="fecha" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="procesadas" name="Procesadas" fill="#013060" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="publicadas" name="Publicadas" fill="#52DDCF" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="border border-border bg-card p-6">
              <div className="kicker mb-4">Por categoría</div>
              {(data?.por_categoria || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">Sin datos todavía.</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={data.por_categoria} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95}>
                      {data.por_categoria.map((entry, i) => (
                        <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div className="border border-border bg-card p-6">
              <div className="kicker mb-4">Producción por medio</div>
              <ul className="space-y-3">
                {(data?.por_medio || []).map((m) => (
                  <li key={m.name} className="flex items-center justify-between text-sm border-b border-border/60 pb-2">
                    <span>{m.name}</span>
                    <span className="mono font-medium">{m.value}</span>
                  </li>
                ))}
                {(data?.por_medio || []).length === 0 && (
                  <li className="text-sm text-muted-foreground">Sin datos todavía.</li>
                )}
              </ul>
            </div>
            <div className="border border-border bg-card p-6">
              <div className="kicker mb-4">Rendimiento de anuncios</div>
              <ul className="space-y-3">
                {(data?.banners || []).map((b) => (
                  <li key={b.name} className="flex items-center justify-between text-sm border-b border-border/60 pb-2">
                    <span className="truncate">{b.name}</span>
                    <span className="mono text-muted-foreground">
                      {b.impresiones} impr · {b.clics} clics
                    </span>
                  </li>
                ))}
                {(data?.banners || []).length === 0 && (
                  <li className="text-sm text-muted-foreground">
                    Aún no hay anuncios.{" "}
                    <Link to="/banners" className="text-primary inline-flex items-center">
                      Crear <ArrowUpRight className="h-3 w-3" />
                    </Link>
                  </li>
                )}
              </ul>
            </div>
          </div>

          <WeeklyRanking />
        </>
      )}
    </div>
  );
}
